import Unity, { UnityContext } from "react-unity-webgl";
import React, { FC, useState, useEffect } from "react";
import styled from "styled-components";
import { webglPrefix } from "../env";
import { useLocation } from "react-router-dom";
import { upperFirst, has } from "lodash";
import { LoadingScreen } from "./LoadingScreen";
import { EndScreen } from "./EndScreen";
import { useGTagEvent } from "./useGTag";

interface UnityFunctions {
  onLoadedChanged?: (isLoaded: boolean) => void;
  onLoadedProgressChanged?: (progress: number) => void;

  onTrackEvent?: (eventName: string, data: { [key: string]: any }) => void;

  onGameIsReady?: (name: string, version: string) => void;
  onGameHasEnded?: () => void;
}

function makeUnityContext(functions: UnityFunctions) {
  let unityContent = new UnityContext({
    loaderUrl: `${webglPrefix}.loader.js`,
    dataUrl: `${webglPrefix}.data`,
    frameworkUrl: `${webglPrefix}.framework.js`,
    codeUrl: `${webglPrefix}.wasm`,
  });

  functions.onLoadedProgressChanged &&
    unityContent.on("progress", functions.onLoadedProgressChanged);

  unityContent.on("loaded", () => {
    functions.onLoadedChanged && functions.onLoadedChanged(true);
  });

  unityContent.on("GameIsReady", (name: string, version: string) => {
    functions.onGameIsReady && functions.onGameIsReady(name, version);
  });

  unityContent.on("GameHasEnded", () => {
    functions.onGameHasEnded && functions.onGameHasEnded();
  });

  unityContent.on("GameTrackEvent", (eventName: string, jsonData: string) => {
    functions.onTrackEvent &&
      functions.onTrackEvent(eventName, JSON.parse(jsonData));
  });
  return unityContent;
}

interface Props {
  onRecievedVersionInfo?: (name: string, version: string) => void;
  className?: string;
  forceEnd?: boolean;
}

const UnityWebGLWrapper: FC<Props> = ({
  onRecievedVersionInfo,
  className,
  forceEnd,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadedProgress, setLoadedProgress] = useState(0);
  const [isEnded, setIsEnded] = useState(false);
  const query = new URLSearchParams(useLocation().search);
  const trackEvent = useGTagEvent();
  const [unityContext] = useState<UnityContext>(
    makeUnityContext({
      onGameIsReady: (name, version) => {
        setIsLoaded(true);
        onRecievedVersionInfo && onRecievedVersionInfo(name, version);
        query.forEach(function (value, key) {
          unityContext.send(
            "WebGLManager",
            `SetURLParameter${upperFirst(key)}`,
            value
          );
        });
        trackEvent("WebGLLoaded");
      },
      onGameHasEnded: () => {
        setIsEnded(true);
        trackEvent("WebGLEnded");
      },
      onLoadedProgressChanged: setLoadedProgress,
      onTrackEvent: (eventName, data) => {
        query.forEach(function (value, key) {
          if (!has(data, upperFirst(key))) {
            data[upperFirst(key)] = value;
          }
        });
        trackEvent(eventName, data);
      },
    })
  );
  useEffect(() => {
    if (forceEnd && !isEnded) {
      setIsEnded(true);
    }
  }, [isEnded, forceEnd, setIsEnded]);

  if (isEnded) {
    return (
      <div className={className}>
        <EndScreen />
      </div>
    );
  }

  return (
    <div className={className}>
      {!isLoaded && <LoadingScreen progress={loadedProgress} />}
      <StyledUnity unityContext={unityContext} />
    </div>
  );
};

const StyledUnity = styled(Unity)`
  width: 100%;
  height: 100%;
  cursor: default;
`;

export { UnityWebGLWrapper };
